import { BudgetEntity, PendingBudgetChange } from './types';
import { formatCurrencyNoUnit } from '@/lib/formatters';

export interface SystemData {
  id: number;
  name: string;
  owner: string;
  owner_id: number;
  department_name: string;
  department_id: number;
  value: number;
  original_currency_value: number;
  original_currency: string;
  budget_id?: number;
}

export interface PendingSystemBudgetChange {
  id: number;
  value: string;
  currency: string;
  budget_id?: number;
}

export const getEffectiveBudgetValue = (
  system: SystemData | BudgetEntity,
  pendingChanges: Map<number, PendingSystemBudgetChange>,
) => {
  const pendingChange = pendingChanges.get(system.id);
  return pendingChange
    ? pendingChange.value
    : system.original_currency_value?.toString();
};

export const getEffectiveBudgetCurrency = (
  system: SystemData | BudgetEntity,
  pendingChanges: Map<number, PendingSystemBudgetChange>,
) => {
  const pendingChange = pendingChanges.get(system.id);
  return pendingChange ? pendingChange.currency : system.original_currency;
};

export const getSavedBudgetValue = (
  system: SystemData | BudgetEntity,
): string => {
  return system.original_currency_value?.toString() || 'No budget set';
};

export const getSavedBudgetCurrency = (
  system: SystemData | BudgetEntity,
): string => {
  return system.original_currency || 'N/A';
};

export const calculateSharePercentage = <T extends BudgetEntity>(
  entity: T,
  totalBudget: number,
  pendingChanges: Map<number, PendingBudgetChange>,
): string => {
  if (!totalBudget || totalBudget === 0) {
    return 'N/A';
  }

  const pendingChange = pendingChanges.get(entity.id);
  const entityBudget = pendingChange
    ? parseFloat(pendingChange.value)
    : entity.value || 0;

  if (entityBudget === 0) {
    return '';
  }

  const sharePercentage = (entityBudget / totalBudget) * 100;
  return `${formatCurrencyNoUnit(sharePercentage)}%`;
};

export const calculateShareInDepartmentBudget = (
  system: SystemData,
  departmentBudgets: Map<
    number,
    { value: number; currency: string; name: string }
  >,
  pendingChanges: Map<number, PendingSystemBudgetChange>,
): string => {
  const departmentBudget = departmentBudgets.get(system.department_id);
  if (!departmentBudget?.value || departmentBudget.value === 0) {
    return 'N/A';
  }

  const pendingChange = pendingChanges.get(system.id);
  const systemBudget = pendingChange
    ? parseFloat(pendingChange.value)
    : system.value || 0;

  if (systemBudget === 0) {
    return '';
  }

  const sharePercentage = (systemBudget / departmentBudget.value) * 100;
  return `${formatCurrencyNoUnit(sharePercentage)}%`;
};

export const calculateTotalAllocated = <T extends BudgetEntity>(
  entities: T[],
): string => {
  let totalBudget = 0;
  entities.forEach((entity) => {
    totalBudget += entity.value || 0;
  });
  return formatCurrencyNoUnit(totalBudget);
};

export const calculateAllocationPercentage = (
  totalAllocated: number,
  totalBudget: number,
): string => {
  if (!totalBudget || totalBudget === 0) {
    return 'N/A';
  }
  const percentage = (totalAllocated / totalBudget) * 100;
  return `${formatCurrencyNoUnit(percentage)}%`;
};

export const calculateUnallocated = (
  totalAllocated: number,
  totalBudget: number,
): string => {
  if (!totalBudget) {
    return 'N/A';
  }
  const unallocated = totalBudget - totalAllocated;
  return formatCurrencyNoUnit(unallocated);
};

export const calculateUnallocatedPercentage = (
  totalAllocated: number,
  totalBudget: number,
): string => {
  if (!totalBudget || totalBudget === 0) {
    return 'N/A';
  }
  const unallocated = totalBudget - totalAllocated;
  const percentage = (unallocated / totalBudget) * 100;
  return `${formatCurrencyNoUnit(percentage)}%`;
};

export const calculateTotalAllocatedFromDepartmentBudget = (
  items: SystemData[],
  selectedDepartmentId: string,
): string => {
  if (!selectedDepartmentId) {
    return 'N/A';
  }

  let totalBudget = 0;
  items
    .filter((system) => system.department_id === parseInt(selectedDepartmentId))
    .forEach((system) => {
      totalBudget += system.value || 0;
    });
  return formatCurrencyNoUnit(totalBudget);
};

export const calculateDepartmentBudgetAllocationPercentage = (
  items: SystemData[],
  userDepartmentId: string,
  departmentBudgets: Map<
    number,
    { value: number; currency: string; name: string }
  >,
): string => {
  if (!userDepartmentId) {
    return 'N/A';
  }

  const departmentBudget = departmentBudgets.get(parseInt(userDepartmentId));
  if (!departmentBudget?.value || departmentBudget.value === 0) {
    return 'N/A';
  }

  const totalAllocated = parseFloat(
    calculateTotalAllocatedFromDepartmentBudget(items, userDepartmentId),
  );
  const percentage = (totalAllocated / departmentBudget.value) * 100;
  return `${formatCurrencyNoUnit(percentage)}%`;
};

export const calculateUnallocatedFromDepartmentBudget = (
  items: SystemData[],
  userDepartmentId: string,
  departmentBudgets: Map<
    number,
    { value: number; currency: string; name: string }
  >,
): string => {
  if (!userDepartmentId) {
    return 'N/A';
  }

  const departmentBudget = departmentBudgets.get(parseInt(userDepartmentId));
  if (!departmentBudget?.value) {
    return 'N/A';
  }

  const totalAllocated = parseFloat(
    calculateTotalAllocatedFromDepartmentBudget(items, userDepartmentId),
  );
  const unallocated = departmentBudget.value - totalAllocated;
  return formatCurrencyNoUnit(unallocated);
};

export const validateBudgetSum = <T extends BudgetEntity>(
  entities: T[],
  totalBudget: number,
  pendingChanges: Map<number, PendingBudgetChange>,
): boolean => {
  if (!totalBudget) return true;

  let totalEntityBudget = 0;
  entities.forEach((entity) => {
    const pendingChange = pendingChanges.get(entity.id);
    const budgetValue = pendingChange
      ? parseFloat(pendingChange.value)
      : entity.value || 0;
    totalEntityBudget += budgetValue;
  });

  return totalEntityBudget <= totalBudget;
};

export const calculateUnallocatedDepartmentPercentage = (
  items: SystemData[],
  userDepartmentId: string,
  departmentBudgets: Map<
    number,
    { value: number; currency: string; name: string }
  >,
): string => {
  if (!userDepartmentId) {
    return 'N/A';
  }

  const departmentBudget = departmentBudgets.get(parseInt(userDepartmentId));
  if (!departmentBudget?.value || departmentBudget.value === 0) {
    return 'N/A';
  }

  const unallocated = parseFloat(
    calculateUnallocatedFromDepartmentBudget(
      items,
      userDepartmentId,
      departmentBudgets,
    ),
  );
  const percentage = (unallocated / departmentBudget.value) * 100;
  return `${formatCurrencyNoUnit(percentage)}%`;
};

export const validateSystemBudgetSum = (
  items: SystemData[],
  departmentId: number,
  departmentBudgets: Map<
    number,
    { value: number; currency: string; name: string }
  >,
  pendingChanges: Map<number, PendingSystemBudgetChange>,
): boolean => {
  const departmentBudget = departmentBudgets.get(departmentId);
  if (!departmentBudget?.value) return true;

  let totalSystemBudget = 0;
  items
    .filter((system) => system.department_id === departmentId)
    .forEach((system) => {
      const pendingChange = pendingChanges.get(system.id);
      const budgetValue = pendingChange
        ? parseFloat(pendingChange.value)
        : system.value || 0;
      totalSystemBudget += budgetValue;
    });
  return totalSystemBudget <= departmentBudget.value;
};

export const validateAllDepartmentBudgets = (
  items: SystemData[],
  departmentBudgets: Map<
    number,
    { value: number; currency: string; name: string }
  >,
  pendingChanges: Map<number, PendingSystemBudgetChange>,
): boolean => {
  const departmentIds = [
    ...new Set(
      items
        .filter((item) => pendingChanges.has(item.id))
        .map((system) => system.department_id),
    ),
  ];
  return departmentIds.every((deptId) =>
    validateSystemBudgetSum(items, deptId, departmentBudgets, pendingChanges),
  );
};
